<ng-template #delta let-d>
  @if (d) {
    <span class="overview-delta">
      @if (d.dir !== 'flat') {
        <span class="overview-delta__arrow" aria-hidden="true">{{ d.dir === 'up' ? '▲' : '▼' }}</span>
        <span class="sr-only">{{ d.dir === 'up' ? 'Up' : 'Down' }}</span>
      }
      {{ d.text }}
    </span>
  }
</ng-template>

<div class="api-card api-card--full">
  <div class="api-card__header">
    <div class="api-card__header-stack">
      <span class="api-card__title">Usage and spend</span>
      <span class="api-card__desc">
        {{ current.range.from | date:'MMM d' }} – {{ current.range.to | date:'MMM d, y' }}@if (hasComparison) {, compared with the {{ rangeDays }} days before}.
      </span>
    </div>
    @if (current.topOrganizations.length) {
      <button type="button" class="btn--secondary" (click)="exportCsv()">Export CSV</button>
    }
  </div>

  <dl class="overview-figures overview-figures--bordered">
    <div class="overview-figure">
      <dt class="overview-figure__label">Requests</dt>
      <dd class="overview-figure__value">{{ current.usage.requests | number }}</dd>
      <dd class="overview-figure__note"><ng-container *ngTemplateOutlet="delta; context: { $implicit: requestsDelta }"></ng-container></dd>
    </div>
    <div class="overview-figure">
      <dt class="overview-figure__label">Credits</dt>
      <dd class="overview-figure__value">{{ current.usage.credits | number }}</dd>
      <dd class="overview-figure__note"><ng-container *ngTemplateOutlet="delta; context: { $implicit: creditsDelta }"></ng-container></dd>
    </div>
    <div class="overview-figure">
      <dt class="overview-figure__label">Cost</dt>
      <dd class="overview-figure__value">{{ current.usage.costCents | money }} {{ currency | uppercase }}</dd>
      <dd class="overview-figure__note"><ng-container *ngTemplateOutlet="delta; context: { $implicit: costDelta }"></ng-container></dd>
    </div>
    <div class="overview-figure">
      <dt class="overview-figure__label">Errors</dt>
      <dd class="overview-figure__value">{{ current.usage.errors | number }} · {{ current.usage.errorRate * 100 | number:'1.1-1' }}%</dd>
      <dd class="overview-figure__note"><ng-container *ngTemplateOutlet="delta; context: { $implicit: errorsDelta }"></ng-container></dd>
    </div>
    <div class="overview-figure">
      <dt class="overview-figure__label">Avg latency</dt>
      <dd class="overview-figure__value">{{ current.usage.avgLatencyMs | latency }}</dd>
      <dd class="overview-figure__note"><ng-container *ngTemplateOutlet="delta; context: { $implicit: latencyDelta }"></ng-container></dd>
    </div>
  </dl>

  @if (sources.length) {
    <div class="source-split">
      <span class="source-split__label">By source</span>
      <div class="source-split__bar" role="img"
           [attr.aria-label]="sourcesLabel">
        @for (s of sources; track s.label) {
          <span class="source-split__segment" [style.flex-grow]="s.value" [style.background]="s.color"></span>
        }
      </div>
      <ul class="source-split__legend">
        @for (s of sources; track s.label) {
          <li>
            <span class="source-split__dot" [style.background]="s.color" aria-hidden="true"></span>
            <span class="source-split__name">{{ s.label }}</span>
            {{ s.value | number }} · {{ s.pct }}%
          </li>
        }
      </ul>
    </div>
  }

  @if (current.topOrganizations.length) {
    <div class="admin-table-scroll">
      <table class="billing-table spend-table admin-stack admin-stack--spend">
        <thead>
          <tr>
            <th class="spend-table__rank">#</th>
            <th>Organization</th>
            <th class="col-num">Requests</th>
            <th>Share of spend</th>
            <th class="col-num">Cost ({{ currency | uppercase }})</th>
            @if (hasComparison) {
              <th class="col-num">vs before</th>
            }
            <th class="col-num">Balance ({{ currency | uppercase }})</th>
          </tr>
        </thead>
        <tbody>
          @for (o of current.topOrganizations; track o.organization.slug; let i = $index) {
            <tr>
              <td class="spend-table__rank">{{ i + 1 }}</td>
              <td>{{ o.organization.name }}</td>
              <td class="col-num">{{ o.requests | number }}</td>
              <td>
                <div class="spend-bar">
                  <div class="spend-bar__track"><div class="spend-bar__fill" [style.width.%]="share(o)"></div></div>
                  <span class="spend-bar__pct">{{ share(o) }}%</span>
                </div>
              </td>
              <td class="col-num">{{ o.costCents | money }}</td>
              @if (hasComparison) {
                <td class="col-num spend-table__muted"><ng-container *ngTemplateOutlet="delta; context: { $implicit: orgDelta(o) }"></ng-container></td>
              }
              <td class="col-num" [class.billing-card__balance--negative]="balanceCents(o) < 0">
                {{ balanceCents(o) | money }}
              </td>
            </tr>
          }
        </tbody>
        <tfoot>
          <tr>
            <td class="spend-table__rank"></td>
            <td>All organizations</td>
            <td class="col-num">{{ current.usage.requests | number }}</td>
            <td></td>
            <td class="col-num">{{ current.usage.costCents | money }}</td>
            @if (hasComparison) {
              <td class="col-num spend-table__muted"><ng-container *ngTemplateOutlet="delta; context: { $implicit: totalCostDelta }"></ng-container></td>
            }
            <td class="col-num" [class.billing-card__balance--negative]="current.wallets.balanceCents < 0">{{ current.wallets.balanceCents | money }}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  } @else {
    <div class="empty-state">
      <p class="empty-state__title">No usage in this window yet.</p>
      <p class="empty-state__desc">Organizations appear here, ranked by spend, once they make calls.</p>
    </div>
  }

  <div class="api-card__footer">
    <a routerLink="/admin/organizations" class="link">
      View all organizations
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M4.5 2.5L8 6L4.5 9.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </a>
  </div>
</div>
